import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AkfaSummaryCards from "./AkfaSummaryCards";
import AkfaNewProductsTab from "./AkfaNewProductsTab";
import AkfaMappingTab from "./AkfaMappingTab";
import AkfaPriceChangesTab from "./AkfaPriceChangesTab";
import AkfaLogSettingsTab from "./AkfaLogSettingsTab";
import { getAkfaProducts, getAkfaSummary } from "../../services/akfa/akfaService";

const SUMMARY_POLL_MS = 60000;

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

export default function AkfaIntegrationLayout() {
    const { t } = useTranslation();
    const { token } = useSelector((state) => state.user);
    const [summary, setSummary] = useState(null);
    const [activeTab, setActiveTab] = useState("newProducts");

    const refreshSummary = async () => {
        // mappedCount/ignoredCount summary'da yo'q — `total` orqali olinadi (hujjat, 1-bo'lim)
        // newCount summary'da doim 0 (ishlatilmaydi) — haqiqiy yangi mahsulotlar soni
        // source=PRODUCT bo'yicha alohida so'rov orqali olinadi (AKFA_FRONTEND_NEW_PRODUCTS.md, 4-band)
        const [summaryRes, mappedRes, ignoredRes, newProductsRes] = await Promise.all([
            getAkfaSummary(token),
            getAkfaProducts(token, { mappingStatus: "MAPPED", size: 1 }),
            getAkfaProducts(token, { mappingStatus: "IGNORED", size: 1 }),
            getAkfaProducts(token, { source: "PRODUCT", size: 1 }),
        ]);
        if (!summaryRes.success) return;
        setSummary({
            ...summaryRes.data,
            mappedCount: mappedRes.success ? mappedRes.data?.total : null,
            ignoredCount: ignoredRes.success ? ignoredRes.data?.total : null,
            newProductsCount: newProductsRes.success ? newProductsRes.data?.total : null,
        });
    };

    useEffect(() => {
        refreshSummary();
        const interval = setInterval(refreshSummary, SUMMARY_POLL_MS);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const tabs = [
        {
            key: "newProducts",
            name: t("akfaIntegrationPage.tabs.new_products"),
            component: <AkfaNewProductsTab refreshSummary={refreshSummary} />,
        },
        {
            key: "priceChanges",
            name: t("akfaIntegrationPage.tabs.price_changes"),
            component: <AkfaPriceChangesTab refreshSummary={refreshSummary} />,
        },
        {
            key: "mapping",
            name: t("akfaIntegrationPage.tabs.mapping"),
            component: <AkfaMappingTab refreshSummary={refreshSummary} />,
        },
        {
            key: "log",
            name: t("akfaIntegrationPage.tabs.log_settings"),
            component: <AkfaLogSettingsTab refreshSummary={refreshSummary} />,
        },
    ];

    return (
        <div className="px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h1 className="text-xl font-semibold text-gray-900">{t("akfaIntegrationPage.title")}</h1>
                {summary && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span
                            className={classNames(
                                "inline-block w-2.5 h-2.5 rounded-full",
                                summary.listenerRunning ? "bg-green-500" : "bg-red-500"
                            )}
                        />
                        {summary.listenerRunning
                            ? t("akfaIntegrationPage.header.listener_running")
                            : t("akfaIntegrationPage.header.listener_stopped")}
                        {summary.lastMessageAt && (
                            <span className="text-gray-400">
                                · {t("akfaIntegrationPage.header.last_message")}{" "}
                                {new Date(summary.lastMessageAt).toLocaleString()}
                            </span>
                        )}
                    </div>
                )}
            </div>

            <AkfaSummaryCards summary={summary} />

            <div className="border-b border-gray-200 mb-2">
                <nav className="flex -mb-px space-x-6">
                    {tabs.map((tab) => (
                        <div
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={classNames(
                                tab.key === activeTab
                                    ? "border-indigo-500 text-gray-900"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                                "whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm cursor-pointer"
                            )}
                        >
                            {tab.name}
                        </div>
                    ))}
                </nav>
            </div>

            {tabs.find((tab) => tab.key === activeTab)?.component}
        </div>
    );
}
